import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Auth, getToken, setToken, type User } from '@/lib/api';
import { useChatStore } from '@/stores/chat';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'cf-chat:token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(getToken()));
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const applyAuth = useCallback((token: string | null, nextUser: User | null) => {
    setToken(token);
    setUser(nextUser);
    // Mirror the user id into the chat store so ConversationListItem can
    // suppress self when computing direct-chat titles without having to
    // thread the User through every list-item consumer.
    useChatStore.getState().setCurrentUserId(nextUser?.id ?? null);
    if (token) try { localStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setLoading(false);
      setUser(null);
      useChatStore.getState().setCurrentUserId(null);
      return;
    }
    try {
      setLoading(true);
      const { user: me } = await Auth.me();
      if (!mountedRef.current) return;
      setUser(me);
      useChatStore.getState().setCurrentUserId(me.id);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to load user';
      setError(message);
      setUser(null);
      setToken(null);
      useChatStore.getState().setCurrentUserId(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    setError(null);
    const { user: u, token } = await Auth.login({ username, password });
    applyAuth(token, u);
    setUser(u);
  }, [applyAuth]);

  const register = useCallback(async (username: string, password: string, displayName?: string) => {
    setError(null);
    const { user: u, token } = await Auth.register({ username, password, displayName });
    applyAuth(token, u);
    setUser(u);
  }, [applyAuth]);

  const logout = useCallback(async () => {
    try {
      await Auth.logout();
    } catch {
      /* even if server logout fails, clear locally */
    }
    applyAuth(null, null);
    setUser(null);
    // Wipe the chat store — otherwise the next user on this browser sees
    // the previous user's drafts / reactions / typing-state until a hard
    // reload. The store's `reset` resets everything except the current
    // user/conversation selectors (those are about to be re-set on
    // re-login anyway).
    useChatStore.getState().reset();
  }, [applyAuth]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, error, login, register, logout, refresh }),
    [user, loading, error, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
